import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CarriedBasketsCard } from "@/components/Dashboard/CarriedBasketsCard";
import { EmaTrendParametersCard } from "@/components/Dashboard/EmaTrendParametersCard";
import { OpenPositionSection } from "@/components/OpenPosition/OpenPositionSection";
import { useAuth } from "@/hooks/useAuth";
import { brokerApi } from "@/lib/brokerApi";
import {
  describeTimeToOpen,
  describeTimeToSquareOff,
  formatClockHHmm,
  formatDateLong,
  greetingForHour,
  marketPhaseAt,
  type MarketPhase,
} from "@/lib/datetime";
import { strategiesApi } from "@/lib/api";
import { tradeLogApi } from "@/lib/tradeLogApi";
import type { KiteStatus } from "@/types/broker";
import { ALL_STRATEGIES, type TradeLogEntry } from "@/types/tradeLog";

const BROKER_KEY = ["broker", "status"] as const;
const STRATEGIES_KEY = ["strategies"] as const;
const CLOCK_MS = 30_000;

export function DashboardPage(): ReactElement {
  const now = useTickingClock();
  const { user } = useAuth();
  const broker = useQuery({
    queryKey: BROKER_KEY,
    queryFn: () => brokerApi.status(),
    refetchInterval: 30_000,
  });
  const strategies = useQuery({
    queryKey: STRATEGIES_KEY,
    queryFn: () => strategiesApi.list(),
  });

  const phase = marketPhaseAt(now);
  const greeting = greetingForHour(now.getHours());
  const name = user ?? "there";
  const connected = broker.data?.connected ?? false;

  return (
    <main className="mx-auto max-w-6xl px-6 py-6">
      <Header
        greeting={greeting}
        name={name}
        now={now}
        phase={phase}
        broker={broker.data ?? null}
      />
      {phase === "pre-market" && (
        <PreMarketBody connected={connected} strategiesReady={(strategies.data?.length ?? 0) > 0} />
      )}
      {phase === "open" && <ActiveBody now={now} />}
      {phase === "closed" && <ClosedBody />}
    </main>
  );
}

function useTickingClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
    }, CLOCK_MS);
    return () => {
      clearInterval(id);
    };
  }, []);
  return now;
}

interface HeaderProps {
  greeting: string;
  name: string;
  now: Date;
  phase: MarketPhase;
  broker: KiteStatus | null;
}

function Header({ greeting, name, now, phase, broker }: Readonly<HeaderProps>): ReactElement {
  const dayComplete = phase === "closed";
  const headline = dayComplete ? "Day completed" : `${greeting}, ${name}`;
  const subtitle = dashboardSubtitle(now, phase);
  return (
    <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="mb-1">{headline}</h1>
        <p className="text-sm text-ink-secondary">{subtitle}</p>
      </div>
      <BrokerPill broker={broker} />
    </header>
  );
}

function dashboardSubtitle(now: Date, phase: MarketPhase): string {
  const date = formatDateLong(now);
  const clock = formatClockHHmm(now);
  const base = `${date} - ${clock} IST`;
  if (phase === "pre-market") {
    const open = describeTimeToOpen(now);
    const suffix = open !== null ? ` - ${open}` : "";
    return base + suffix;
  }
  if (phase === "open") return `${base} - Market open`;
  return `${base} - Market closed`;
}

function BrokerPill({ broker }: Readonly<{ broker: KiteStatus | null }>): ReactElement {
  if (broker === null) {
    return (
      <span className="pill bg-surface-2 text-ink-secondary">
        <i className="ti ti-loader-2 animate-spin text-sm" /> Loading
      </span>
    );
  }
  if (broker.connected) {
    return (
      <span className="pill bg-success-bg text-success">
        <i className="ti ti-circle-check text-sm" />
        Broker connected - Zerodha
      </span>
    );
  }
  return (
    <span className="pill bg-warning-bg text-warning">
      <i className="ti ti-plug-off text-sm" />
      Broker disconnected
    </span>
  );
}

function PreMarketBody({
  connected,
  strategiesReady,
}: Readonly<{ connected: boolean; strategiesReady: boolean }>): ReactElement {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <ActionCard
        to="/broker"
        icon="ti-link"
        title={connected ? "Broker connected" : "Connect to broker"}
        subtitle={connected ? "Daily login done" : "Daily ritual - takes ~30 sec"}
        done={connected}
      />
      <ActionCard
        to="/strategies"
        icon="ti-target"
        title={strategiesReady ? "Strategies armed" : "Add or arm a strategy"}
        subtitle={strategiesReady ? "Webhooks ready" : "Pick today's bias"}
        done={strategiesReady}
      />
    </div>
  );
}

interface ActionCardProps {
  to: string;
  icon: string;
  title: string;
  subtitle: string;
  done: boolean;
}

function ActionCard({ to, icon, title, subtitle, done }: Readonly<ActionCardProps>): ReactElement {
  return (
    <Link
      to={to}
      className="card flex items-center gap-3 transition-colors hover:border-accent-border"
    >
      <div
        className={
          done
            ? "flex h-10 w-10 items-center justify-center rounded-full bg-success-bg text-success"
            : "flex h-10 w-10 items-center justify-center rounded-full bg-accent-bg text-accent"
        }
      >
        <i className={`ti ${done ? "ti-circle-check" : icon} text-xl`} />
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-ink-secondary">{subtitle}</div>
      </div>
      <i className="ti ti-chevron-right text-lg text-ink-muted" />
    </Link>
  );
}

function useTodaysTrades() {
  return useQuery({
    queryKey: ["dashboard-trades-today"] as const,
    queryFn: async () => {
      const result = await tradeLogApi.list({
        range: "today",
        strategyId: ALL_STRATEGIES,
        show: "trades",
      });
      return result.trades;
    },
    refetchInterval: 30_000,
  });
}

interface DayStats {
  pnl: number | null;
  count: number;
  closedCount: number;
  winRate: number | null;
  bestTrade: number | null;
  avgTrailSteps: number | null;
}

interface RunningTotals {
  netPnl: number;
  closedCount: number;
  wins: number;
  best: number | null;
  trailSum: number;
}

function accumulate(acc: RunningTotals, t: TradeLogEntry): RunningTotals {
  if (t.exitReason === "OPEN") return acc;
  const next: RunningTotals = {
    ...acc,
    closedCount: acc.closedCount + 1,
    trailSum: acc.trailSum + t.trailCount,
  };
  if (t.pnl === null) return next;
  const net = t.pnl - (t.brokerage ?? 0);
  next.netPnl += net;
  if (net > 0) next.wins += 1;
  if (acc.best === null || net > acc.best) next.best = net;
  return next;
}

function computeStats(trades: TradeLogEntry[]): DayStats {
  const totals = trades.reduce<RunningTotals>(accumulate, {
    netPnl: 0,
    closedCount: 0,
    wins: 0,
    best: null,
    trailSum: 0,
  });
  const closedCount = totals.closedCount;
  return {
    pnl: closedCount === 0 ? null : totals.netPnl,
    count: trades.length,
    closedCount,
    winRate: closedCount === 0 ? null : Math.round((totals.wins / closedCount) * 100),
    bestTrade: totals.best,
    avgTrailSteps: closedCount === 0 ? null : Math.round((totals.trailSum / closedCount) * 10) / 10,
  };
}

function formatPnl(v: number | null): string {
  if (v === null) return "-";
  const abs = Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  return v >= 0 ? `+Rs ${abs}` : `-Rs ${abs}`;
}

function pnlClass(v: number | null): string | undefined {
  if (v === null || v === 0) return undefined;
  return v > 0 ? "text-success" : "text-danger";
}

function ActiveBody({ now }: Readonly<{ now: Date }>): ReactElement {
  const squareOff = describeTimeToSquareOff(now) ?? "-";
  const q = useTodaysTrades();
  const stats = useMemo(() => computeStats(q.data ?? []), [q.data]);
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Today's P&L"
          value={formatPnl(stats.pnl)}
          hint={stats.pnl === null ? "No closed trades yet" : "Net (after brokerage)"}
          valueClass={pnlClass(stats.pnl)}
        />
        <StatCard
          label="Trades today"
          value={String(stats.count)}
          hint={`${String(stats.closedCount)} closed`}
        />
        <StatCard
          label="Win rate"
          value={stats.winRate === null ? "-" : `${String(stats.winRate)}%`}
          hint={stats.winRate === null ? "Not enough data" : `${String(stats.closedCount)} closed`}
        />
        <StatCard label="Square off in" value={squareOff} hint="Forced at 15:24" />
      </div>
      <OpenPositionSection showStrategyLink />
      {/* Read before a signal fires: the ATR band and State 3 both move with
          the session's volatility, so they are worth knowing in advance. */}
      <EmaTrendParametersCard />
      {/* Overnight baskets (docs/positional.md) — hidden while none open. */}
      <CarriedBasketsCard />
    </div>
  );
}

function ClosedBody(): ReactElement {
  const q = useTodaysTrades();
  const stats = useMemo(() => computeStats(q.data ?? []), [q.data]);
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Day's P&L"
          value={formatPnl(stats.pnl)}
          hint={stats.pnl === null ? "No trades today" : "Net (after brokerage)"}
          valueClass={pnlClass(stats.pnl)}
        />
        <StatCard label="Trades closed" value={String(stats.closedCount)} />
        <StatCard
          label="Best trade"
          value={formatPnl(stats.bestTrade)}
          valueClass={pnlClass(stats.bestTrade)}
        />
        <StatCard
          label="Avg trail steps"
          value={stats.avgTrailSteps === null ? "-" : `x${String(stats.avgTrailSteps)}`}
        />
      </div>
      {/* Overnight baskets survive the close by design — after hours is
          exactly when the operator wants to see what is being carried. */}
      <CarriedBasketsCard />
      <div className="card">
        <div className="mb-1 flex items-center gap-2">
          <i className="ti ti-history text-lg text-accent" />
          <h3>Recap</h3>
        </div>
        <p className="text-sm text-ink-secondary">
          Detailed breakdown per closed trade lives on the Trade Log page.
        </p>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  valueClass,
}: Readonly<{
  label: string;
  value: string;
  hint?: string;
  valueClass?: string | undefined;
}>): ReactElement {
  return (
    <div className="stat-card">
      <div className="text-xs text-ink-secondary">{label}</div>
      <div className={`mt-1 text-2xl font-medium ${valueClass ?? ""}`}>{value}</div>
      {hint !== undefined && <div className="mt-1 text-xs text-ink-muted">{hint}</div>}
    </div>
  );
}
